import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../../components/common/form/DateField/DatePickerStyles.css'
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import {
  getWhoData,
  axisConfig,
  type ChartType,
  type ChartSex,
  type PatientDataPoint
} from '../../components/PatientDossier/components/GrowthChart/data'
import ChartControls from '../../components/PatientDossier/components/GrowthChart/ChartControls'
import ChartDisplay from '../../components/PatientDossier/components/GrowthChart/ChartDisplay'
import growthMessages from '../../components/PatientDossier/components/GrowthChart/messages'
import messages from './messages'
import LandingNavbar from '../../components/LandingNavbar/LandingNavbar'
import { trackEvent } from '../../services/analytics'

dayjs.extend(customParseFormat)

interface MeasurementRow {
  id: string
  date: string
  weight: string
  height: string
  ofc: string
}

function ageInMonths(dob: string, visitTimestamp: number): number | null {
  if (!dob) return null
  const birth = dayjs(dob, 'DD.MM.YYYY')
  if (!birth.isValid()) return null
  const months = dayjs(visitTimestamp).diff(birth, 'month', true)
  if (months < 0 || months > 60) return null
  return months
}

function extractPoints(
  rows: MeasurementRow[],
  chartType: ChartType,
  dob: string
): PatientDataPoint[] {
  const points: PatientDataPoint[] = []

  for (const row of rows) {
    if (!row.date) continue
    const ts = new Date(row.date).getTime()
    const months = ageInMonths(dob, ts)
    if (months === null) continue

    let value: number | null = null

    if (chartType === 'weightForAge') {
      const w = parseFloat(row.weight)
      if (!isNaN(w) && w > 0) value = w
    } else if (chartType === 'heightForAge') {
      const h = parseFloat(row.height)
      if (!isNaN(h) && h > 0) value = h
    } else if (chartType === 'bmiForAge') {
      const w = parseFloat(row.weight)
      const h = parseFloat(row.height)
      if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) {
        value = Math.round((w / Math.pow(h / 100, 2)) * 10) / 10
      }
    } else if (chartType === 'headCircumferenceForAge') {
      const hc = parseFloat(row.ofc)
      if (!isNaN(hc) && hc > 0) value = hc
    }

    if (value !== null) {
      points.push({
        month: Math.round(months * 10) / 10,
        value,
        visitDate: dayjs(row.date).format('DD MMM YYYY')
      })
    }
  }

  return points.sort((a, b) => a.month - b.month)
}

interface DatePickerInputProps {
  value: string
  onChange: (val: string) => void
  outputFormat?: 'DD.MM.YYYY' | 'YYYY-MM-DD'
  w?: string
  size?: 'sm' | 'md'
}

function DatePickerInput({
  value,
  onChange,
  outputFormat = 'YYYY-MM-DD',
  w = '150px',
  size = 'sm'
}: DatePickerInputProps) {
  const selected = (() => {
    if (!value) return null
    let d = dayjs(value, 'DD.MM.YYYY')
    if (!d.isValid()) d = dayjs(value, 'YYYY-MM-DD')
    return d.isValid() ? d.toDate() : null
  })()

  return (
    <DatePicker
      selected={selected}
      onChange={(date: Date | null) => {
        if (!date) {
          onChange('')
          return
        }
        onChange(dayjs(date).format(outputFormat))
      }}
      dateFormat="dd.MM.yyyy"
      placeholderText="DD.MM.YYYY"
      autoComplete="off"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      maxDate={new Date()}
      customInput={
        <Input size={size} borderColor="gray.200" borderRadius="8px" w={w} />
      }
    />
  )
}

// Example data — a boy born June 2024, 4 measurements at WHO 50th percentile
const EXAMPLE_DOB = '01.06.2024'
const EXAMPLE_SEX: ChartSex = 'M'
const EXAMPLE_ROWS: MeasurementRow[] = [
  {
    id: 'ex-0',
    date: '2024-06-01',
    weight: '3.3',
    height: '49.9',
    ofc: '34.5'
  },
  {
    id: 'ex-1',
    date: '2024-12-01',
    weight: '7.9',
    height: '67.6',
    ofc: '43.3'
  },
  {
    id: 'ex-2',
    date: '2025-06-01',
    weight: '10.2',
    height: '76.1',
    ofc: '46.5'
  },
  {
    id: 'ex-3',
    date: '2025-12-01',
    weight: '11.7',
    height: '82.3',
    ofc: '48.4'
  }
]

let rowCounter = 0

function makeRow(): MeasurementRow {
  return {
    id: String(Date.now() + rowCounter++),
    date: '',
    weight: '',
    height: '',
    ofc: ''
  }
}

export function GrowthChartsLanding() {
  const { formatMessage } = useIntl()

  const [sex, setSex] = useState<ChartSex>(EXAMPLE_SEX)
  const [dob, setDob] = useState(EXAMPLE_DOB)
  const [chartType, setChartType] = useState<ChartType>('weightForAge')
  const [rows, setRows] = useState<MeasurementRow[]>(EXAMPLE_ROWS)

  function clearAll() {
    setDob('')
    setRows([makeRow()])
  }

  const percentiles = getWhoData(chartType, sex)
  const config = axisConfig[chartType]
  const patientPoints = extractPoints(rows, chartType, dob)

  const measurementLabel = formatMessage(
    growthMessages[chartType as keyof typeof growthMessages] as Parameters<
      typeof formatMessage
    >[0]
  )

  function updateRow(id: string, field: keyof MeasurementRow, value: string) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    )
  }

  function removeRow(id: string) {
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
    )
  }

  const pageTitle = formatMessage(messages.pageTitle)
  const metaDescription = formatMessage(messages.metaDescription)

  return (
    <Box minH="100vh" bg="gray.50" pt="64px" overflowX="hidden">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="child growth chart, WHO growth standards, pediatric growth, weight for age, height for age, head circumference"
        />
        <link rel="canonical" href="https://sijilo.com/growth-charts" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content="https://sijilo.com/growth-charts" />
      </Helmet>

      <LandingNavbar />

      {/* Hero */}
      <Box
        bg="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        py={{ base: 10, md: 14 }}
        px={6}
        textAlign="center"
      >
        <Box maxW="680px" mx="auto">
          <Box
            display="inline-flex"
            alignItems="center"
            gap="6px"
            px="14px"
            py="6px"
            borderRadius="8px"
            fontSize="0.8rem"
            fontWeight="500"
            color="gray.700"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            mb={5}
          >
            <Text as="span" fontSize="0.65rem">
              📈
            </Text>{' '}
            WHO Growth Standards
          </Box>
          <Text
            as="h1"
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="800"
            letterSpacing="-0.04em"
            lineHeight="1.1"
            color="gray.900"
            mb={4}
          >
            {formatMessage(messages.heroHeadline)}
          </Text>
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            color="gray.500"
            maxW="520px"
            mx="auto"
            lineHeight="1.7"
          >
            {formatMessage(messages.heroSubheadline)}
          </Text>
        </Box>
      </Box>

      {/* Main content */}
      <Box maxW="1120px" mx="auto" px={4} py={8}>
        <VStack spacing={4} align="stretch" minW={0}>
          {/* Patient info */}
          <Box
            bg="white"
            minW={0}
            borderRadius="10px"
            border="1px solid"
            borderColor="gray.200"
            p={6}
            _hover={{
              borderColor: 'gray.300',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}
            transition="border-color 0.2s, box-shadow 0.2s"
          >
            <Text
              fontWeight="600"
              fontSize="lg"
              letterSpacing="-0.02em"
              color="gray.900"
              mb={4}
            >
              {formatMessage(messages.patientInfoTitle)}
            </Text>
            <Flex gap={6} wrap="wrap" align="flex-end">
              <FormControl w="auto">
                <FormLabel fontSize="sm" color="gray.600">
                  {formatMessage(messages.sexLabel)}
                </FormLabel>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    isActive={sex === 'M'}
                    onClick={() => {
                      setSex('M')
                      trackEvent('growth_chart_interaction', {
                        action: 'sex_change',
                        value: 'M'
                      })
                    }}
                    borderColor="gray.200"
                    borderRadius="8px 0 0 8px"
                    _active={{
                      bg: 'blue.500',
                      color: 'white',
                      borderColor: 'blue.500'
                    }}
                  >
                    {formatMessage(messages.male)}
                  </Button>
                  <Button
                    isActive={sex === 'F'}
                    onClick={() => {
                      setSex('F')
                      trackEvent('growth_chart_interaction', {
                        action: 'sex_change',
                        value: 'F'
                      })
                    }}
                    borderColor="gray.200"
                    borderRadius="0 8px 8px 0"
                    _active={{
                      bg: 'pink.400',
                      color: 'white',
                      borderColor: 'pink.400'
                    }}
                  >
                    {formatMessage(messages.female)}
                  </Button>
                </ButtonGroup>
              </FormControl>

              <FormControl w="200px">
                <FormLabel fontSize="sm" color="gray.600">
                  {formatMessage(messages.dobLabel)}
                </FormLabel>
                <DatePickerInput
                  value={dob}
                  onChange={setDob}
                  outputFormat="DD.MM.YYYY"
                  w="200px"
                />
              </FormControl>
            </Flex>
          </Box>

          {/* Measurements table */}
          <Box
            bg="white"
            minW={0}
            overflow="hidden"
            borderRadius="10px"
            border="1px solid"
            borderColor="gray.200"
            p={6}
            _hover={{
              borderColor: 'rgba(37,99,235,0.15)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
            }}
            transition="border-color 0.2s, box-shadow 0.2s"
          >
            <Text
              fontWeight="600"
              fontSize="lg"
              letterSpacing="-0.02em"
              color="gray.900"
              mb={4}
            >
              {formatMessage(messages.measurementsTitle)}
            </Text>
            <Box overflowX="auto" maxW="full" overscrollBehaviorX="contain">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th color="gray.500">
                      {formatMessage(messages.dateColumn)}
                    </Th>
                    <Th color="gray.500">
                      {formatMessage(messages.weightColumn)}
                    </Th>
                    <Th color="gray.500">
                      {formatMessage(messages.heightColumn)}
                    </Th>
                    <Th color="gray.500">
                      {formatMessage(messages.ofcColumn)}
                    </Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row) => (
                    <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <DatePickerInput
                          value={row.date}
                          onChange={(v) => {
                            updateRow(row.id, 'date', v)
                          }}
                          outputFormat="YYYY-MM-DD"
                          w="150px"
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          size="sm"
                          borderColor="gray.200"
                          borderRadius="8px"
                          value={row.weight}
                          onChange={(e) => {
                            updateRow(row.id, 'weight', e.target.value)
                          }}
                          placeholder="e.g. 9.5"
                          w="100px"
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          size="sm"
                          borderColor="gray.200"
                          borderRadius="8px"
                          value={row.height}
                          onChange={(e) => {
                            updateRow(row.id, 'height', e.target.value)
                          }}
                          placeholder="e.g. 75"
                          w="100px"
                        />
                      </Td>
                      <Td>
                        <Input
                          type="number"
                          size="sm"
                          borderColor="gray.200"
                          borderRadius="8px"
                          value={row.ofc}
                          onChange={(e) => {
                            updateRow(row.id, 'ofc', e.target.value)
                          }}
                          placeholder="e.g. 46"
                          w="100px"
                        />
                      </Td>
                      <Td>
                        <IconButton
                          aria-label={formatMessage(messages.removeRow)}
                          icon={<span>✕</span>}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            removeRow(row.id)
                          }}
                          isDisabled={rows.length === 1}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Flex mt={3} gap={2}>
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.200"
                borderRadius="8px"
                color="gray.700"
                _hover={{ bg: 'gray.50' }}
                onClick={() => {
                  setRows((prev) => [...prev, makeRow()])
                  trackEvent('growth_chart_interaction', {
                    action: 'add_measurement_row',
                    row_count: rows.length + 1
                  })
                }}
              >
                {formatMessage(messages.addRow)}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                borderRadius="8px"
                color="gray.400"
                _hover={{ bg: 'gray.50', color: 'gray.600' }}
                onClick={clearAll}
              >
                {formatMessage(messages.clearAll)}
              </Button>
            </Flex>
          </Box>

          {/* Chart */}
          <Box
            bg="white"
            borderRadius="10px"
            border="1px solid"
            borderColor="gray.200"
            pb={4}
            _hover={{
              borderColor: 'rgba(37,99,235,0.15)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
            }}
            transition="border-color 0.2s, box-shadow 0.2s"
          >
            <Text
              fontWeight="600"
              fontSize="lg"
              letterSpacing="-0.02em"
              color="gray.900"
              px={8}
              pt={6}
              mb={2}
            >
              {formatMessage(messages.chartTitle)}
            </Text>
            <ChartControls
              chartType={chartType}
              sex={sex}
              onChartTypeChange={setChartType}
              onSexChange={setSex}
            />
            <ChartDisplay
              percentiles={percentiles}
              patientPoints={patientPoints}
              yLabel={config.yLabel}
              yMin={config.yMin}
              yMax={config.yMax}
              measurementLabel={measurementLabel}
            />
          </Box>
        </VStack>
      </Box>

      {/* CTA */}
      <Box py={{ base: 16, md: 20 }} px={6} bg="white">
        <Box maxW="1120px" mx="auto">
          <Box
            textAlign="center"
            px={{ base: 6, md: 10 }}
            py={{ base: 12, md: 16 }}
            borderRadius="20px"
            bg="#f8f9fb"
            border="1px solid"
            borderColor="gray.200"
          >
            <Text
              as="h2"
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="700"
              letterSpacing="-0.03em"
              color="gray.900"
              mb={3}
            >
              {formatMessage(messages.ctaHeadline)}
            </Text>
            <Text color="gray.500" mb={8} maxW="500px" mx="auto">
              {formatMessage(messages.ctaDescription)}
            </Text>
            <Button
              as="a"
              href="https://calendar.notion.so/meet/mostafaelgayar/l72vw4ohd"
              target="_blank"
              rel="noopener noreferrer"
              bg="gray.900"
              color="white"
              fontWeight="600"
              px="32px"
              py="14px"
              h="auto"
              borderRadius="10px"
              fontSize="md"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
              sx={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
              onClick={() => {
                trackEvent('cta_click', { label: 'growth_charts_book_demo' })
              }}
            >
              {formatMessage(messages.ctaButton)}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
